import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CTO Directive #127 §A — MEOS Live Room receiver view.
 *
 * This is the page a trusted physical display (TV browser via the
 * real Cast driver, or any device the user points at the short
 * code) opens. It is intentionally PUBLIC — the join happens by
 * short-code that the sender read out; joining IS the physical
 * proof the session layer requires before saying "Yayın başladı".
 *
 * Honesty: a clear "MEOS Live Room" banner + preview-cast notice.
 * No fake video claims — frames arrive at preview cadence.
 */
export default function WatchRoomPage() {
  const { code } = useParams();
  const [joined, setJoined] = useState(null); // null=trying, false=failed
  const [frame, setFrame] = useState(null); // {src, mime}
  const [stopped, setStopped] = useState(false);
  const timersRef = useRef({ hb: 0, frame: 0 });

  const join = useCallback(async () => {
    try {
      await axios.post(`${API}/me/cast/live-room/${code}/join`);
      setJoined(true);
    } catch {
      setJoined(false);
    }
  }, [code]);

  useEffect(() => {
    join();
  }, [join]);

  useEffect(() => {
    if (!joined) return undefined;
    const hb = setInterval(async () => {
      try {
        await axios.post(`${API}/me/cast/live-room/${code}/heartbeat`);
      } catch {
        setStopped(true);
      }
    }, 4000);
    const fr = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/me/cast/live-room/${code}/frame`);
        const d = r.data || {};
        if (d.frame_base64) {
          setFrame({ src: `data:${d.mime || "image/jpeg"};base64,${d.frame_base64}` });
        }
      } catch {
        /* no frame yet — keep the calm waiting view */
      }
    }, 1500);
    timersRef.current = { hb, frame: fr };
    return () => { clearInterval(hb); clearInterval(fr); };
  }, [joined, code]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-6 text-slate-100"
      data-testid="watch-room-page"
    >
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between px-6 py-4 text-xs text-slate-400">
        <span className="mono uppercase tracking-[0.2em]" data-testid="watch-room-banner">
          MEOS Live Room
        </span>
        <span className="mono text-[10px] tracking-[0.16em]" data-testid="watch-room-code">
          {code}
        </span>
      </div>

      {joined === null && (
        <p className="text-sm text-slate-400" data-testid="watch-room-joining">
          Odaya bağlanıyorum…
        </p>
      )}
      {joined === false && (
        <p className="max-w-md text-center text-sm text-slate-300" data-testid="watch-room-failed">
          Bu yayın kodunu doğrulayamadım. Kod süresi dolmuş ya da yayın
          sonlanmış olabilir.
        </p>
      )}
      {joined && stopped && (
        <p className="text-sm text-slate-300" data-testid="watch-room-stopped">
          Yayın sonlandı.
        </p>
      )}
      {joined && !stopped && (
        frame ? (
          <img
            src={frame.src}
            alt="Yayın içeriği"
            className="max-h-[80vh] max-w-[92vw] rounded-lg shadow-2xl"
            data-testid="watch-room-frame"
          />
        ) : (
          <div className="flex flex-col items-center gap-3" data-testid="watch-room-waiting">
            <div className="h-3 w-3 animate-pulse rounded-full bg-sky-300/80" />
            <p className="text-sm text-slate-400">İçerik hazırlanıyor…</p>
          </div>
        )
      )}

      <p
        className="absolute bottom-4 left-0 right-0 text-center text-[10px] uppercase tracking-[0.2em] text-slate-600"
        data-testid="watch-room-notice"
      >
        MEOS Live Room Preview Cast — düşük kareli önizleme yayını
      </p>
    </div>
  );
}
